import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Skeleton } from "primereact/skeleton";
import * as client from "../../../client";
import { useToast } from "../../../context-providers/Toast";
import { RecipientEditor } from "../components/RecipientEditor";
import { useClearanceRecipients, useLocalUser, useUpdateReport } from "../data";
import {
  BorStepInfo,
  borEmailTargetReportId,
  borStepLabel,
  useBorEmailPreview,
  useSendBorEmail,
} from "./data";
import styles from "./BorEmailDialog.module.css";

type BorEmailDialogProps = {
  visible: boolean;
  onHide: () => void;
  bor: client.BorLetterResponse;
  step: BorStepInfo["key"];
};

/** Confirmation modal that previews and sends the BOR broker email for a step. */
export const BorEmailDialog: React.FC<BorEmailDialogProps> = ({
  visible,
  onHide,
  bor,
  step,
}) => {
  const toast = useToast();
  const localUser = useLocalUser();
  const targetReportId = borEmailTargetReportId(bor, step);
  const updateReport = useUpdateReport(targetReportId);
  const sendEmail = useSendBorEmail();

  const recipientsQuery = useClearanceRecipients(targetReportId, visible);
  const previewQuery = useBorEmailPreview(bor.id, step, visible);

  const recipients = recipientsQuery.data?.recipients ?? [];
  const lockedEmails = recipientsQuery.data?.locked_emails ?? [];
  const mutating = updateReport.isPending || recipientsQuery.isFetching;
  const sending = sendEmail.isPending;

  const toCurrentBroker = step.startsWith("outgoing_");
  const roleLabel = toCurrentBroker
    ? "current broker of record"
    : "blocked broker";

  const persistRecipients = (nextEditable: string[]) => {
    updateReport.mutate(
      { submissionRecipients: nextEditable },
      {
        onError: () => {
          toast.show({
            severity: "error",
            summary: "Failed to save recipients",
          });
        },
      }
    );
  };

  const send = () => {
    sendEmail.mutate(
      { borId: bor.id, step },
      {
        onSuccess: () => {
          toast.show({
            severity: "success",
            summary: "Email Sent",
            detail: `BOR email sent to ${recipients.length} recipient(s)`,
          });
          onHide();
        },
        onError: () => {
          toast.show({
            severity: "error",
            summary: "Email Failed",
            detail: "Failed to send BOR email",
          });
        },
      }
    );
  };

  return (
    <Dialog
      header={borStepLabel(step)}
      visible={visible}
      onHide={() => {
        if (sending) return;
        onHide();
      }}
      style={{ width: "560px" }}
      footer={
        <div className={styles.footer}>
          <Button
            label="Cancel"
            severity="secondary"
            text
            onClick={onHide}
            disabled={sending}
          />
          <Button
            label="Send email"
            icon="pi pi-send"
            onClick={send}
            loading={sending}
            disabled={
              mutating || recipients.length === 0 || previewQuery.isError
            }
          />
        </div>
      }
    >
      <div className={styles.roleNote}>
        This email will be sent to the <strong>{roleLabel}</strong> on report{" "}
        <strong>{targetReportId}</strong>.
        {localUser?.email ? (
          <>
            {" "}
            You will be Cc'd at <strong>{localUser.email}</strong>.
          </>
        ) : null}
      </div>

      <RecipientEditor
        recipients={recipients}
        lockedEmails={lockedEmails}
        isLoading={recipientsQuery.isPending}
        mutating={mutating}
        disabled={sending}
        onPersist={persistRecipients}
      />

      <div className={styles.sectionLabel}>Subject</div>
      {previewQuery.isPending ? (
        <Skeleton height="36px" />
      ) : previewQuery.isError ? (
        <div className={styles.subjectBox}>Failed to load email preview.</div>
      ) : (
        <div className={styles.subjectBox}>{previewQuery.data?.subject}</div>
      )}

      <div className={styles.sectionLabel}>Preview</div>
      {previewQuery.isPending ? (
        <Skeleton height="200px" />
      ) : previewQuery.isError ? (
        <div className={styles.subjectBox}>
          Could not render the email preview.
        </div>
      ) : (
        // Sandboxed (no allow-scripts/allow-same-origin) so the email HTML
        // renders isolated from the app and can't execute anything.
        <iframe
          className={styles.bodyPreview}
          title="Email preview"
          sandbox=""
          srcDoc={previewQuery.data?.body_html ?? ""}
        />
      )}
    </Dialog>
  );
};
